<div class="contacts">
  <app-page-header breadcrumb="Administration" [doubleArrows]="true" title="Contacts" [keepIconOnMobile]="true">
  </app-page-header>
  <app-admin-tabs
    active="contacts"
    [contactsCount]="allContactsCount"
    [organisationsCount]="organisationsCount"></app-admin-tabs>
  @if (isTableInitiating) {
    <app-action-buttons-skeleton></app-action-buttons-skeleton>
    <app-table-skeleton></app-table-skeleton>
  } @else {
    <div class="contacts__action-buttons">
      <app-contact-filters [columns]="columnsToDisplay" (filterApplied)="onFilterChange($event)"></app-contact-filters>
      <span class="contacts__count">{{ totalCount }} contacts</span>
      <button class="contacts__add-button" mat-flat-button [matMenuTriggerFor]="addContactMenu">
        <mat-icon>add</mat-icon>
        ADD CONTACT
        <span class="sgl-arrows" aria-hidden="true"></span>
      </button>
      <mat-menu #addContactMenu="matMenu" class="contacts__add-menu">
        <button mat-menu-item class="contacts__add-menu-item" (click)="createContact('Person')">
          <div class="contacts__add-menu-item-content">
            <div class="contacts__add-menu-item-icon">
              <mat-icon class="material-symbols-outlined">person</mat-icon>
            </div>
            <div class="contacts__add-menu-item-text">
              <span class="contacts__add-menu-item-title">Person</span>
              <span class="contacts__add-menu-item-subtitle">Link an existing user as a contact</span>
            </div>
          </div>
        </button>
        <button mat-menu-item class="contacts__add-menu-item" (click)="createContact('Department')">
          <div class="contacts__add-menu-item-content">
            <div class="contacts__add-menu-item-icon">
              <mat-icon class="material-symbols-outlined">domain</mat-icon>
            </div>
            <div class="contacts__add-menu-item-text">
              <span class="contacts__add-menu-item-title">Department</span>
              <span class="contacts__add-menu-item-subtitle">Create a shared department contact</span>
            </div>
          </div>
        </button>
      </mat-menu>
    </div>
    <div class="contacts__table-wrapper">
      <!--
        The header lives OUTSIDE the cdk-virtual-scroll-viewport on purpose: the viewport fakes
        scroll position by applying `transform: translateY(...)` to its content wrapper, and that
        transform creates a new containing block for anything `position: sticky` inside it — so a
        sticky header rendered inside the viewport ends up pinned to the (moving) wrapper instead
        of the real scrollport and visibly drifts down while scrolling. Keeping the header as a
        separate, non-scrolling row above the viewport avoids the problem entirely.

        Being a separate element means it doesn't get horizontal scroll for free when the table
        goes wider than the viewport at narrow screens — onViewportScroll() below manually syncs
        this row's scrollLeft to the viewport's. It's not independently scrollable itself
        (overflow-x: hidden in the SCSS): scrollLeft is only ever driven programmatically here.

        Split into an outer clip + inner row on purpose: #headerRow (outer) stays at the
        table-wrapper's actual width and is what scrollLeft gets set on; contacts__header-row-inner
        is the one forced to $contacts-table-min-width, same as .contacts__table. A single element
        can't play both roles — "clip to container width" and "be wider than the container" are
        contradictory on the same box.

        This scroll is scoped to the grid only (viewport + this header), NOT the action-buttons
        row above — that row deliberately stays outside it, always fully visible, regardless of
        how far the grid itself is scrolled. See $contacts-table-min-width's comment in the SCSS
        for why: an earlier version scoped this to the whole page instead, which dragged the
        search box off-screen while scrolling to see far-right grid columns.
      -->
      <div #headerRow class="contacts__header-row">
        <div class="contacts__header-row-inner" matSort (matSortChange)="onSort($event)" role="row">
          <div
            class="contacts__header-cell contacts__header-cell--displayName"
            mat-sort-header="displayName"
            role="columnheader">
            {{ columnsToDisplayMap.get('displayName') }}
          </div>
          <div class="contacts__header-cell contacts__header-cell--email" mat-sort-header="email" role="columnheader">
            {{ columnsToDisplayMap.get('email') }}
          </div>
          <div class="contacts__header-cell contacts__header-cell--jobTitle" role="columnheader">
            {{ columnsToDisplayMap.get('jobTitle') }}
          </div>
          <div class="contacts__header-cell contacts__header-cell--phone" role="columnheader">
            {{ columnsToDisplayMap.get('phone') }}
          </div>
          <div class="contacts__header-cell contacts__header-cell--type" mat-sort-header="type" role="columnheader">
            {{ columnsToDisplayMap.get('type') }}
          </div>
          <div class="contacts__header-cell contacts__header-cell--actions" role="columnheader">Actions</div>
        </div>
      </div>
      <cdk-virtual-scroll-viewport
        [itemSize]="rowHeight"
        class="contacts__viewport"
        (scroll)="onViewportScroll($event)">
        <table
          mat-table
          recycleRows
          [dataSource]="dataSource"
          class="contacts__table"
          [attr.aria-rowcount]="totalCount">
          <colgroup>
            @for (column of displayedColumns; track column) {
              <col class="contacts__col--{{ column }}" />
            }
          </colgroup>
          <ng-container matColumnDef="displayName">
            <td mat-cell *matCellDef="let element">
              <div class="contacts__name-cell">
                @if (productName(element); as product) {
                  <span class="contacts__product-avatar">
                    <img [ngSrc]="product | transportModeIcon" width="16" height="16" alt="" />
                  </span>
                } @else {
                  <app-avatar
                    [displayName]="element.displayName"
                    [photoUrl]="element.photoUrl"
                    [color]="avatarColor(element.type)"
                    [textColor]="avatarTextColor(element.type)"></app-avatar>
                }
                <app-is-active [isActive]="element.isActive" [dot]="true"></app-is-active>
                <span class="contacts__name-text" [appEllipsisTooltip]="element.displayName">{{
                  element.displayName
                }}</span>
              </div>
            </td>
          </ng-container>
          <ng-container matColumnDef="email">
            <td mat-cell *matCellDef="let element">{{ element.email }}</td>
          </ng-container>
          <ng-container matColumnDef="jobTitle">
            <td mat-cell *matCellDef="let element">{{ element.jobTitle ?? '--' }}</td>
          </ng-container>
          <ng-container matColumnDef="phone">
            <td mat-cell *matCellDef="let element">
              <app-phone-cell [phone]="element.phone" [mobilePhone]="element.mobilePhone"></app-phone-cell>
            </td>
          </ng-container>
          <ng-container matColumnDef="type">
            <td mat-cell *matCellDef="let element">
              <app-contact-type [type]="element.type"></app-contact-type>
            </td>
          </ng-container>
          <ng-container matColumnDef="actions">
            <td mat-cell *matCellDef="let element">
              <div class="contacts__actions-cell">
                <button
                  matIconButton
                  class="contacts__action-btn"
                  aria-label="Edit contact"
                  [disabled]="isLoading"
                  (click)="editContact(element)">
                  <mat-icon class="material-symbols-outlined">edit</mat-icon>
                </button>
                @if (isUserAdmin$ | async) {
                  <button
                    matIconButton
                    class="contacts__action-btn"
                    aria-label="Delete contact"
                    [disabled]="isLoading"
                    (click)="deleteContact(element)">
                    <mat-icon class="material-symbols-outlined">delete</mat-icon>
                  </button>
                }
              </div>
            </td>
          </ng-container>
          <tr mat-row *matRowDef="let row; columns: displayedColumns" [attr.aria-rowindex]="row.rowIndex + 2"></tr>
        </table>
      </cdk-virtual-scroll-viewport>
    </div>
    @if (!isLoading && totalCount === 0) {
      <div class="contacts__empty-state">
        {{ hasError ? 'Failed to load contacts. Please try again.' : 'No data found' }}
      </div>
    }
    @if (isLoading) {
      <div class="spinner">
        <mat-spinner diameter="60"></mat-spinner>
      </div>
    }
  }
</div>
